import React, { useState } from "react";
import useCustomTablePageSizeOptions from "components/common/table/useCustomTablePageSizeOptions";

import EmptyTableData from "components/common/empty_data/EmptyTableData";
import { Search as SearchIcon } from "@mui/icons-material";

const useCustomTable = ({ data, columns, options, props }) => {
  const { tablePageSize, rowsPerPageOptions, showRowsPerPage, page } = options;

  const [pageSize, setPageSize, pageSizeOptions] =
    useCustomTablePageSizeOptions();

  const [pagination, setPagination] = useState({
    pageIndex: page || 0,
    pageSize: tablePageSize || pageSize,
  });

  const handlePagination = (e) => {
    setPagination(e);
    setPageSize(pagination.pageSize);
  };

  const defaultOptions = {
    data,
    columns,
    enableHiding: true,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableExpandAll: false,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
    }),
    state: {
      pagination,
      columnOrder: [],
    },
    enableSorting: false,
    onPaginationChange: handlePagination,
    enableToolbarInternalActions: false,
    enableFilterMatchHighlighting: false,
    enableColumnResizing: false,
    displayColumnDefOptions: {
      "mrt-row-expand": {
        header: "",
      },
    },
    muiPaginationProps: {
      showFirstButton: true,
      showLastButton: true,
      rowsPerPageOptions: rowsPerPageOptions || pageSizeOptions,
      showRowsPerPage: showRowsPerPage,
      sx: {
        display: "none",
      },
    },
    muiTopToolbarProps: {
      sx: {
        borderBottom: "1px solid #eee",
      },
    },
    muiBottomToolbarProps: {
      boxShadow: "transparent !important",
    },
    muiToolbarAlertBannerProps: {
      sx: {
        display: "none",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        ".Mui-TableHeadCell-Content-Wrapper": {
          textOverflow: "clip !important",
          color: "rgba(0, 0, 0, 0.87)",
          fontSize: "0.875rem",
          fontWeight: 500,
        },
        ".MuiCheckbox-root > .MuiSvgIcon-root ": {
          fontSize: "1.5rem !important",
        },
        ".MuiSvgIcon-root": {
          fontSize: "1rem",
        },
        ".MuiIconButton-root": {
          padding: "2px 6px",
        },
        ".MuiOutlinedInput-root": {
          paddingRight: "2px",
          fontSize: "0.875rem",
        },
      },
    },
    muiFilterCheckboxProps: {
      title: "",
    },
    muiCircularProgressProps: {
      size: 0,
    },
    renderEmptyRowsFallback: () => <EmptyTableData />,
    muiFilterTextFieldProps: ({ column }) => ({
      variant: "outlined",
      placeholder: "",
      sx: {
        m: "0.5rem 0",
        p: "0 5px 0 0",
        width: "100%",
        size: "small",
        "&.MuiFormControl-root": {
          minWidth: "auto !important",
        },
        "&.MuiInputBase-root-MuiOutlinedInput-root": {
          padding: "0 !important",
        },
        ".MuiSelect-outlined": {
          minHeight: "auto !important",
          padding: "8px",
        },
      },
      inputProps: {
        style: {
          padding: "8px 0 8px 8px",
        },
      },
      InputProps: {
        startAdornment: column?.columnDef?.filterVariant === "text" && (
          <SearchIcon />
        ),
      },
    }),
    muiTableProps: () => ({
      sx: {
        ".MuiTableCell-body": {
          fontWeight: 400,
          fontSize: "0.875rem",
          lineHeight: 1.43,
          letterSpacing: "0.01071em",
          verticalAlign: "inherit",
          textAlign: "left",
          padding: "16px",
          color: "rgba(0, 0, 0, 0.87)",
          borderBottom: "1px solid #E0E0E0 !important",
          wordBreak: "break-word",
        },
        ".MuiOutlinedInput-input::placeholder": {
          fontSize: "0.875rem",
        },
      },
    }),
    muiTablePaperProps: () => ({
      sx: {
        border: "1px solid rgb(224, 224, 224)",
        borderRadius: 0,
        boxShadow: "none",
        marginRight: "15px",
      },
    }),
    positionToolbarDropZone: "none",
    renderBottomToolbarCustomActions: () => (
      <div
        style={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "flex-end",
          marginRight: "1rem",
        }}
      >
        <span>Rows per page:</span>
      </div>
    ),
    muiDetailPanelProps: () => ({
      sx: {
        backgroundColor: "#FFF",
        padding: "1px !important",
        paddingLeft: "1px !important",
      },
    }),
  };

  const isServerSide = props?.options?.manualFiltering;

  if (isServerSide) {
    defaultOptions["layoutMode"] = "grid";
  }

  if (!isServerSide) {
    defaultOptions.muiTableProps = {
      sx: {
        ".MuiTableCell-body": {
          fontWeight: 400,
          fontSize: "0.875rem",
          lineHeight: 1.43,
          letterSpacing: "0.01071em",
          verticalAlign: "inherit",
          textAlign: "left",
          padding: "16px",
          color: "rgba(0, 0, 0, 0.87)",
          borderBottom: "1px solid #E0E0E0 !important",
          minWidth: "auto",
          maxWidth: "none",
          display: "table-cell",
          wordBreak: "break-word",
        },
        ".MuiOutlinedInput-input::placeholder": {
          fontSize: "0.875rem",
        },
      },
    };
  }

  return { defaultOptions };
};

export default useCustomTable;
